import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Target, Shield, AlertTriangle, ChevronRight, ChevronDown,
  Crosshair, TrendingUp, Lock, Unlock, Eye, EyeOff, Zap,
  ArrowRight, Info, TriangleAlert, CheckCircle2, XCircle, MinusCircle,
  BookMarked, Sparkles, ArrowLeft,
} from "lucide-react";

const BUYER_ROLES = [
  "CEO", "Founder / CEO", "Board Chair", "MD", "COO",
  "Group CHRO", "CFO", "Lead Investor / PE Partner",
  "Integration Lead", "DFI Programme Director",
  "Head of Diversity & Inclusion", "HR Manager",
];
const SECTORS = [
  "Financial Services", "Technology", "Energy & Resources", "Telecommunications",
  "Development Finance", "Professional Services", "Public Sector",
  "FMCG", "Manufacturing", "Healthcare",
];
const MATURITIES = [
  "Start-up / Early Stage", "Scaling / High-Growth",
  "Transition / Restructuring", "Institutional / Established", "Mature / Stable",
];
const TENSIONS = [
  "New ExCo lacks shared operating picture",
  "CEO cannot gauge real leadership capacity",
  "Board demanding leadership bench strength data",
  "Middle managers are bottlenecking execution",
  "CEO frustrated that strategy is not translating into delivery",
  "High performer attrition driven by poor line management",
  "About to scale but unsure if culture will hold",
  "Investors or board asking about organisational readiness",
  "Previous growth phase broke internal cohesion",
  "Female talent exits at middle management — glass ceiling effect",
  "ESG / DFI requirements for gender-balanced leadership",
  "Founder is the single point of failure for all major decisions",
  "Board or investors pushing for institutional governance",
  "Growth is stalling because everything requires founder approval",
  "Two cultures colliding after merger — no shared identity",
  "Trust deficit between legacy teams and incoming leadership",
  "Key talent leaving because they don't recognise the new organisation",
];

const CONFIDENCE_STYLES = {
  "High": { bg: "bg-emerald-900/30", border: "border-emerald-700/50", text: "text-emerald-400", icon: CheckCircle2 },
  "Medium": { bg: "bg-amber-900/20", border: "border-amber-700/40", text: "text-amber-400", icon: MinusCircle },
  "Low": { bg: "bg-orange-900/20", border: "border-orange-700/40", text: "text-orange-400", icon: TriangleAlert },
  "Insufficient": { bg: "bg-red-900/20", border: "border-red-700/40", text: "text-red-400", icon: XCircle },
};

const POSTURE_STYLES = {
  "Authority-led direct outreach": { bg: "bg-emerald-500/15", text: "text-emerald-400", border: "border-emerald-500/30" },
  "Insight-led conversation": { bg: "bg-sky-500/15", text: "text-sky-400", border: "border-sky-500/30" },
  "Referral-led introduction": { bg: "bg-violet-500/15", text: "text-violet-400", border: "border-violet-500/30" },
  "Nurture first, then engage": { bg: "bg-amber-500/15", text: "text-amber-400", border: "border-amber-500/30" },
  "No action yet": { bg: "bg-slate-500/15", text: "text-slate-400", border: "border-slate-500/30" },
};

const CATEGORY_STYLES = {
  "Clear Fit": { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/20" },
  "Ambiguous": { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/20" },
  "Conflicting Signals": { bg: "bg-orange-500/10", text: "text-orange-400", border: "border-orange-500/20" },
  "No Fit": { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/20" },
};

// ─── Small reusable components ────────────────────────────────

function SectionLabel({ children }) {
  return <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2">{children}</p>;
}

function ScoreBar({ score, label }) {
  const pct = Math.round(score * 100);
  const color = score >= 0.7 ? "bg-emerald-500" : score >= 0.5 ? "bg-amber-500" : score >= 0.3 ? "bg-orange-500" : "bg-red-500/60";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] text-slate-500 w-8 text-right font-mono">{pct}%</span>
      {label && <span className="text-[10px] text-slate-600 truncate max-w-[120px]">{label}</span>}
    </div>
  );
}


// ─── Main Page ────────────────────────────────────────────────

export default function EntryIntelligence() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const enrichBriefId = searchParams.get("enrich");
  
  const [input, setInput] = useState({
    buyer_role: "", sector: "", org_maturity: "",
    primary_tension: "", secondary_tensions: [], org_size: "", additional_context: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scenarios, setScenarios] = useState(null);
  const [activeScenario, setActiveScenario] = useState(null);
  const [view, setView] = useState("analyze"); // analyze | scenarios | enrich
  
  // Enrichment mode state
  const [enrichmentContext, setEnrichmentContext] = useState(null);
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);
  const [prefillTracker, setPrefillTracker] = useState({ prefilled: [], userCompleted: [], overridden: [] });

  // Fetch enrichment context if in enrichment mode
  useEffect(() => {
    if (enrichBriefId) {
      setView("enrich");
      setEnrichmentLoading(true);
      fetch(`${API}/commercial-briefs/${enrichBriefId}/enrichment-context`, { credentials: "include" })
        .then(r => r.ok ? r.json() : null)
        .then(ctx => {
          if (ctx) {
            setEnrichmentContext(ctx);
            // Pre-fill fields from context
            const newInput = { ...input };
            const prefilled = [];
            
            if (ctx.ready_fields?.sector) {
              newInput.sector = ctx.ready_fields.sector;
              prefilled.push("sector");
            }
            if (ctx.ready_fields?.inferred_buyer_role) {
              newInput.buyer_role = ctx.ready_fields.inferred_buyer_role;
              prefilled.push("buyer_role");
            }
            // Map tension hypothesis to closest tension if possible
            if (ctx.ready_fields?.tension_hypothesis) {
              // For now, leave primary_tension empty but show the hypothesis
              newInput.additional_context = ctx.ready_fields.tension_hypothesis;
            }
            
            setInput(newInput);
            setPrefillTracker(prev => ({ ...prev, prefilled }));
          }
        })
        .catch(() => toast.error("Failed to load enrichment context"))
        .finally(() => setEnrichmentLoading(false));
    }
  }, [enrichBriefId]);

  useEffect(() => {
    if (!enrichBriefId) {
      fetch(`${API}/entry-intelligence/scenarios`, { credentials: "include" })
        .then(r => r.ok ? r.json() : null)
        .then(d => d && setScenarios(d.scenarios))
        .catch(() => {});
    }
  }, [enrichBriefId]);

  const analyze = async (overrideInput) => {
    const body = overrideInput || input;
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/entry-intelligence/analyze`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) setResult(await res.json());
    } catch {} finally { setLoading(false); }
  };

  const enrichBrief = async () => {
    if (!enrichBriefId || !result) return;
    
    // Track which fields user completed
    const userCompleted = [];
    if (!prefillTracker.prefilled.includes("buyer_role") && input.buyer_role) userCompleted.push("buyer_role");
    if (!prefillTracker.prefilled.includes("sector") && input.sector) userCompleted.push("sector");
    if (input.org_maturity) userCompleted.push("org_maturity");
    if (input.primary_tension) userCompleted.push("primary_tension");
    if (input.org_size) userCompleted.push("org_size");
    
    // Track overrides (if user changed a prefilled value)
    const overridden = [];
    if (prefillTracker.prefilled.includes("sector") && input.sector !== enrichmentContext?.ready_fields?.sector) {
      overridden.push("sector");
    }
    if (prefillTracker.prefilled.includes("buyer_role") && input.buyer_role !== enrichmentContext?.ready_fields?.inferred_buyer_role) {
      overridden.push("buyer_role");
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/commercial-briefs/${enrichBriefId}/enrich`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          buyer_role: input.buyer_role,
          org_maturity: input.org_maturity,
          primary_tension: input.primary_tension,
          org_size: input.org_size ? parseInt(input.org_size) : null,
          secondary_tensions: input.secondary_tensions,
          budget_signals: input.additional_context,
          analysis_result: result,
          prefilled_fields: prefillTracker.prefilled,
          user_completed_fields: userCompleted,
          overridden_fields: overridden,
        }),
      });
      if (res.ok) {
        toast.success("Brief enriched with analysis");
        navigate(`/commercial-briefs/${enrichBriefId}`);
      } else {
        toast.error("Failed to enrich brief");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  const runScenario = async (scenario) => {
    setActiveScenario(scenario.id);
    setInput(scenario.input);
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/entry-intelligence/scenarios/${scenario.id}/analyze`, {
        method: "POST", credentials: "include",
      });
      if (res.ok) setResult(await res.json());
    } catch {} finally { setLoading(false); }
  };

  const updateField = (field, value) => setInput(prev => ({ ...prev, [field]: value }));
  const toggleSecondary = (t) => {
    setInput(prev => ({
      ...prev,
      secondary_tensions: prev.secondary_tensions.includes(t)
        ? prev.secondary_tensions.filter(x => x !== t)
        : [...prev.secondary_tensions, t],
    }));
  };

  return (
    <div className="min-h-screen bg-[#0a0e17] text-slate-200" data-testid="entry-intelligence-page">
      {/* Header */}
      <div className="border-b border-slate-800/60 bg-[#0d1220]">
        <div className="max-w-[1400px] mx-auto px-6 py-5">
          {/* Back button for enrichment mode */}
          {enrichBriefId && (
            <Button size="sm" variant="ghost" className="text-slate-500 hover:text-slate-300 -ml-2 mb-2"
              onClick={() => navigate(`/commercial-briefs/${enrichBriefId}`)} data-testid="back-to-brief-btn">
              <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to Brief
            </Button>
          )}
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2.5 mb-1">
                {enrichBriefId ? (
                  <Sparkles className="w-5 h-5 text-violet-500" />
                ) : (
                  <Crosshair className="w-5 h-5 text-emerald-500" />
                )}
                <h1 className="text-lg font-bold tracking-tight text-slate-100" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="page-title">
                  {enrichBriefId ? "Enrich Commercial Brief" : "Entry-Point Intelligence"}
                </h1>
                {enrichBriefId ? (
                  <Badge variant="outline" className="text-[9px] border-violet-700/50 text-violet-400 ml-1">ENRICHMENT</Badge>
                ) : (
                  <Badge variant="outline" className="text-[9px] border-slate-700 text-slate-500 ml-1">PREVIEW</Badge>
                )}
              </div>
              <p className="text-xs text-slate-500 max-w-xl">
                {enrichBriefId 
                  ? "Complete the missing fields to run a structured analysis on this captured opportunity."
                  : "Commercial judgement layer — determines the most powerful opening proposition for a given buyer situation before any outreach begins."
                }
              </p>
            </div>
            {!enrichBriefId && (
              <div className="flex gap-1">
                <Button size="sm" variant={view === "analyze" ? "default" : "ghost"}
                  className={view === "analyze" ? "bg-slate-800 text-slate-200 hover:bg-slate-700" : "text-slate-500 hover:text-slate-300"}
                  onClick={() => setView("analyze")} data-testid="view-analyze-btn">
                  <Target className="w-3.5 h-3.5 mr-1.5" /> Analyze
                </Button>
                <Button size="sm" variant={view === "scenarios" ? "default" : "ghost"}
                  className={view === "scenarios" ? "bg-slate-800 text-slate-200 hover:bg-slate-700" : "text-slate-500 hover:text-slate-300"}
                  onClick={() => setView("scenarios")} data-testid="view-scenarios-btn">
                  <Shield className="w-3.5 h-3.5 mr-1.5" /> Validation Harness
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-6">
        {view === "scenarios" ? (
          <ScenarioHarness scenarios={scenarios} onRun={runScenario} activeId={activeScenario} result={result} loading={loading} />
        ) : view === "enrich" && enrichmentContext ? (
          <EnrichmentView 
            context={enrichmentContext}
            input={input}
            updateField={updateField}
            toggleSecondary={toggleSecondary}
            onAnalyze={() => analyze()}
            onEnrich={enrichBrief}
            loading={loading || enrichmentLoading}
            result={result}
            prefillTracker={prefillTracker}
          />
        ) : (
          <div className="grid grid-cols-12 gap-6">
            {/* Left: Input Panel */}
            <div className="col-span-4">
              <InputPanel input={input} updateField={updateField} toggleSecondary={toggleSecondary}
                onAnalyze={() => analyze()} loading={loading} />
            </div>
            {/* Right: Output Panel */}
            <div className="col-span-8">
              {loading ? (
                <div className="flex items-center justify-center py-32">
                  <div className="animate-pulse text-slate-500 text-sm">Analyzing buyer situation...</div>
                </div>
              ) : result ? (
                <OutputPanel result={result} />
              ) : (
                <EmptyState />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Enrichment View ──────────────────────────────────────────

function EnrichmentView({ context, input, updateField, toggleSecondary, onAnalyze, onEnrich, loading, result, prefillTracker }) {
  const [showSecondary, setShowSecondary] = useState(false);
  
  const readyFields = context?.ready_fields || {};
  const initialCapture = context?.initial_capture || {};
  const hasPriorAnalysis = context?.has_prior_analysis || false;
  
  // Check if required fields are complete
  const requiredComplete = input.buyer_role && input.org_maturity && input.primary_tension;

  return (
    <div className="grid grid-cols-12 gap-6" data-testid="enrichment-view">
      {/* Left: Context + Input Panel */}
      <div className="col-span-4 space-y-4">
        {/* Original Capture Context */}
        <Card className="bg-violet-950/20 border-violet-800/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-violet-300" style={{ fontFamily: "Manrope, sans-serif" }}>
              Original Capture
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-xs">
            <div>
              <span className="text-slate-500">Account:</span>
              <span className="ml-2 text-slate-200 font-medium">{context?.account_name}</span>
            </div>
            {initialCapture.initial_tension_hypothesis && (
              <div>
                <span className="text-slate-500">Initial Hypothesis:</span>
                <p className="mt-1 text-slate-300 italic">"{initialCapture.initial_tension_hypothesis}"</p>
              </div>
            )}
            {initialCapture.known_buyer_contact && (
              <div>
                <span className="text-slate-500">Contact:</span>
                <span className="ml-2 text-slate-300">{initialCapture.known_buyer_contact}</span>
              </div>
            )}
            {initialCapture.opportunity_source && (
              <div>
                <span className="text-slate-500">Source:</span>
                <span className="ml-2 text-slate-300 capitalize">{initialCapture.opportunity_source.replace(/_/g, ' ')}</span>
              </div>
            )}
            {hasPriorAnalysis && (
              <div className="pt-2 border-t border-violet-800/30">
                <Badge className="text-[9px] bg-violet-900/30 text-violet-400 border-violet-700/30 border">
                  {context?.prior_analysis_count} prior analysis{context?.prior_analysis_count > 1 ? 'es' : ''}
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Field Status */}
        <Card className="bg-[#111827] border-slate-800/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-300" style={{ fontFamily: "Manrope, sans-serif" }}>
              Field Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-xs">
            {/* Ready Fields */}
            {(readyFields.sector || readyFields.inferred_buyer_role || readyFields.tension_hypothesis) && (
              <div>
                <div className="flex items-center gap-1.5 text-emerald-400 mb-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span className="font-semibold">Ready (pre-filled)</span>
                </div>
                <ul className="space-y-1 ml-5 text-slate-400">
                  {readyFields.sector && <li>Sector: {readyFields.sector}</li>}
                  {readyFields.inferred_buyer_role && <li>Buyer Role (inferred): {readyFields.inferred_buyer_role}</li>}
                  {readyFields.tension_hypothesis && <li>Tension context available</li>}
                </ul>
              </div>
            )}

            {/* Required Fields */}
            <div>
              <div className="flex items-center gap-1.5 text-amber-400 mb-1.5">
                <TriangleAlert className="w-3.5 h-3.5" />
                <span className="font-semibold">Required for Analysis</span>
              </div>
              <ul className="space-y-1 ml-5 text-slate-400">
                <li className={input.buyer_role ? "text-emerald-400" : ""}>
                  {input.buyer_role ? "✓" : "○"} Buyer Role
                </li>
                <li className={input.org_maturity ? "text-emerald-400" : ""}>
                  {input.org_maturity ? "✓" : "○"} Organisation Maturity
                </li>
                <li className={input.primary_tension ? "text-emerald-400" : ""}>
                  {input.primary_tension ? "✓" : "○"} Primary Tension
                </li>
              </ul>
            </div>

            {/* Optional Fields */}
            <div>
              <div className="flex items-center gap-1.5 text-slate-500 mb-1.5">
                <Info className="w-3.5 h-3.5" />
                <span className="font-semibold">Optional (improves confidence)</span>
              </div>
              <ul className="space-y-1 ml-5 text-slate-500">
                <li>○ Organisation Size</li>
                <li>○ Secondary Tensions</li>
                <li>○ Budget Signals</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Input Form */}
        <Card className="bg-[#111827] border-slate-800/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-slate-300" style={{ fontFamily: "Manrope, sans-serif" }}>
              Complete Analysis Inputs
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <SectionLabel>Buyer Role *</SectionLabel>
              <select className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-violet-600"
                value={input.buyer_role} onChange={e => updateField("buyer_role", e.target.value)} data-testid="enrich-buyer-role">
                <option value="">— Select —</option>
                {BUYER_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              {readyFields.inferred_buyer_role && input.buyer_role !== readyFields.inferred_buyer_role && (
                <p className="text-[10px] text-slate-500 mt-1">Inferred: {readyFields.inferred_buyer_role}</p>
              )}
            </div>
            <div>
              <SectionLabel>Sector {readyFields.sector && "(pre-filled)"}</SectionLabel>
              <select className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-slate-600"
                value={input.sector} onChange={e => updateField("sector", e.target.value)} data-testid="enrich-sector">
                <option value="">— Select —</option>
                {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <SectionLabel>Organisation Maturity *</SectionLabel>
              <select className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-violet-600"
                value={input.org_maturity} onChange={e => updateField("org_maturity", e.target.value)} data-testid="enrich-maturity">
                <option value="">— Select —</option>
                {MATURITIES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <SectionLabel>Primary Tension *</SectionLabel>
              <select className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-violet-600"
                value={input.primary_tension} onChange={e => updateField("primary_tension", e.target.value)} data-testid="enrich-tension">
                <option value="">— Select —</option>
                {TENSIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              {readyFields.tension_hypothesis && (
                <p className="text-[10px] text-slate-500 mt-1 italic">From capture: "{readyFields.tension_hypothesis.slice(0, 60)}..."</p>
              )}
            </div>
            <div>
              <SectionLabel>Approx. Organisation Size</SectionLabel>
              <input className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-slate-600"
                placeholder="e.g. 350, 2000+" value={input.org_size} onChange={e => updateField("org_size", e.target.value)} data-testid="enrich-org-size" />
            </div>

            {/* Secondary tensions toggle */}
            <button type="button" className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-400"
              onClick={() => setShowSecondary(prev => !prev)}>
              {showSecondary ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              Secondary Tensions (optional)
            </button>
            {showSecondary && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {TENSIONS.filter(t => t !== input.primary_tension).map(t => (
                  <button key={t} type="button"
                    className={`text-[10px] px-2 py-1 rounded-md border transition-colors ${
                      input.secondary_tensions.includes(t)
                        ? "bg-violet-500/20 border-violet-500/40 text-violet-300"
                        : "bg-slate-800/50 border-slate-700/50 text-slate-500 hover:text-slate-400"
                    }`}
                    onClick={() => toggleSecondary(t)}>
                    {t.slice(0, 40)}...
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="space-y-2">
          <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white" size="sm"
            onClick={onAnalyze} disabled={!requiredComplete || loading} data-testid="enrich-analyze-btn">
            <Target className="w-3.5 h-3.5 mr-1.5" />
            {loading ? "Analyzing..." : "Run Analysis"}
          </Button>
          {result && (
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" size="sm"
              onClick={onEnrich} disabled={loading} data-testid="enrich-save-btn">
              <BookMarked className="w-3.5 h-3.5 mr-1.5" />
              Save to Brief
            </Button>
          )}
        </div>
      </div>

      {/* Right: Output Panel */}
      <div className="col-span-8">
        {loading ? (
          <div className="flex items-center justify-center py-32">
            <div className="animate-pulse text-violet-400 text-sm">Analyzing buyer situation...</div>
          </div>
        ) : result ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-violet-800/40 bg-violet-950/20 p-3">
              <div className="flex items-center gap-2 text-sm text-violet-300">
                <Sparkles className="w-4 h-4" />
                <span className="font-semibold">Analysis Complete</span>
                <span className="text-xs text-violet-400">— Click "Save to Brief" to enrich this commercial object</span>
              </div>
            </div>
            <OutputPanel result={result} />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-32 text-center">
            <Sparkles className="w-10 h-10 text-violet-700 mb-3" />
            <p className="text-sm text-slate-500">Complete the required fields and run analysis</p>
            <p className="text-xs text-slate-600 mt-1">The analysis will be saved back to the commercial brief</p>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Input Panel ──────────────────────────────────────────────

function InputPanel({ input, updateField, toggleSecondary, onAnalyze, loading }) {
  const [showSecondary, setShowSecondary] = useState(false);

  return (
    <div className="space-y-4" data-testid="input-panel">
      <Card className="bg-[#111827] border-slate-800/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-slate-300" style={{ fontFamily: "Manrope, sans-serif" }}>
            Buyer Situation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <SectionLabel>Buyer Role</SectionLabel>
            <select className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-slate-600"
              value={input.buyer_role} onChange={e => updateField("buyer_role", e.target.value)} data-testid="input-buyer-role">
              <option value="">— Select —</option>
              {BUYER_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <SectionLabel>Sector</SectionLabel>
            <select className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-slate-600"
              value={input.sector} onChange={e => updateField("sector", e.target.value)} data-testid="input-sector">
              <option value="">— Select —</option>
              {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <SectionLabel>Organisation Maturity</SectionLabel>
            <select className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-slate-600"
              value={input.org_maturity} onChange={e => updateField("org_maturity", e.target.value)} data-testid="input-maturity">
              <option value="">— Select —</option>
              {MATURITIES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <SectionLabel>Approx. Organisation Size</SectionLabel>
            <input className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-slate-600"
              placeholder="e.g. 350, 2000+" value={input.org_size} onChange={e => updateField("org_size", e.target.value)} data-testid="input-org-size" />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#111827] border-slate-800/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-slate-300" style={{ fontFamily: "Manrope, sans-serif" }}>
            Business Tension
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <SectionLabel>Primary Tension</SectionLabel>
            <select className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-slate-600"
              value={input.primary_tension} onChange={e => updateField("primary_tension", e.target.value)} data-testid="input-primary-tension">
              <option value="">— Select primary buyer tension —</option>
              {TENSIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div>
            <button className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold hover:text-slate-400"
              onClick={() => setShowSecondary(!showSecondary)}>
              {showSecondary ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              Secondary Tensions ({input.secondary_tensions.length})
            </button>
            {showSecondary && (
              <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                {TENSIONS.filter(t => t !== input.primary_tension).map(t => (
                  <label key={t} className="flex items-start gap-2 text-xs text-slate-400 hover:text-slate-300 cursor-pointer py-0.5">
                    <input type="checkbox" className="mt-0.5 accent-emerald-500" checked={input.secondary_tensions.includes(t)} onChange={() => toggleSecondary(t)} />
                    <span className="leading-tight">{t}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <SectionLabel>Additional Context</SectionLabel>
            <textarea className="w-full bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-slate-600 resize-none"
              rows={3} placeholder="Any other relevant context about the buyer or organisation..."
              value={input.additional_context} onChange={e => updateField("additional_context", e.target.value)} data-testid="input-context" />
          </div>
        </CardContent>
      </Card>

      <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" disabled={loading}
        onClick={onAnalyze} data-testid="analyze-btn">
        {loading ? "Analyzing..." : "Analyze Entry Point"}
      </Button>
    </div>
  );
}


// ─── Output Panel ─────────────────────────────────────────────

function OutputPanel({ result }) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [accountName, setAccountName] = useState("");
  const [showSave, setShowSave] = useState(false);
  const rec = result.recommendation;
  const hold = result.hold_state;
  const posture = result.commercial_posture;
  const cStyle = CONFIDENCE_STYLES[rec.confidence_label] || CONFIDENCE_STYLES["Insufficient"];
  const pStyle = POSTURE_STYLES[posture.posture] || POSTURE_STYLES["No action yet"];
  const ConfIcon = cStyle.icon;

  const saveBrief = async () => {
    if (!accountName.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/commercial-briefs/from-analysis`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_name: accountName.trim(), analysis_result: result }),
      });
      if (res.ok) {
        const brief = await res.json();
        setSavedId(brief.brief_id);
        toast.success("Commercial Brief saved");
      } else {
        toast.error("Failed to save brief");
      }
    } catch { toast.error("Network error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="output-panel">
      {/* Save as Brief */}
      {!savedId ? (
        <div className="flex items-center gap-2">
          {showSave ? (
            <>
              <input className="flex-1 bg-slate-900 border border-slate-700/60 rounded-md px-3 py-1.5 text-sm text-slate-300 focus:outline-none focus:border-emerald-600"
                placeholder="Account / opportunity name..." value={accountName} onChange={e => setAccountName(e.target.value)}
                onKeyDown={e => e.key === "Enter" && saveBrief()} autoFocus data-testid="brief-account-name-input" />
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveBrief} disabled={saving || !accountName.trim()} data-testid="save-brief-confirm-btn">
                {saving ? "Saving..." : "Save"}
              </Button>
              <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => setShowSave(false)}>Cancel</Button>
            </>
          ) : (
            <Button size="sm" variant="outline" className="border-slate-700 text-slate-400 hover:text-emerald-400 hover:border-emerald-700"
              onClick={() => setShowSave(true)} data-testid="save-as-brief-btn">
              <BookMarked className="w-3.5 h-3.5 mr-1.5" /> Save as Commercial Brief
            </Button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Badge className="bg-emerald-900/30 text-emerald-400 border-emerald-700/40 border text-xs">Brief saved</Badge>
          <Button size="sm" variant="ghost" className="text-emerald-400 text-xs hover:text-emerald-300"
            onClick={() => navigate(`/commercial-briefs/${savedId}`)} data-testid="view-saved-brief-btn">
            View Brief <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      )}

      {/* Hold State Warning */}
      {hold.active && (
        <div className={`rounded-lg border p-4 ${hold.type === "poor_commercial_fit" ? "bg-red-950/30 border-red-800/40" : "bg-amber-950/20 border-amber-800/30"}`}
          data-testid="hold-state-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${hold.type === "poor_commercial_fit" ? "text-red-400" : "text-amber-400"}`} />
            <div>
              <p className={`text-sm font-semibold ${hold.type === "poor_commercial_fit" ? "text-red-300" : "text-amber-300"}`}>
                {hold.type === "poor_commercial_fit" ? "Poor Commercial Fit" : hold.type === "weak_fit" ? "Weak Fit Detected" : "Insufficient Information"}
              </p>
              <p className="text-xs text-slate-400 mt-1">{hold.message}</p>
              <p className="text-xs font-semibold text-slate-300 mt-2">{hold.action_guidance}</p>
            </div>
          </div>
        </div>
      )}

      {/* Primary Recommendation + Confidence + Posture Row */}
      <div className="grid grid-cols-12 gap-4">
        {/* Recommendation */}
        <div className="col-span-8">
          <Card className={`bg-[#111827] border-slate-800/60 ${hold.active ? "opacity-75" : ""}`}>
            <CardContent className="pt-5 pb-4">
              <SectionLabel>Recommended Entry Point</SectionLabel>
              <h2 className="text-xl font-bold text-slate-100 mt-1" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="recommended-title">
                {rec.title}
              </h2>
              <p className="text-xs text-slate-400 mt-1">{rec.subtitle}</p>
              <div className="mt-3 p-3 rounded-md bg-slate-900/60 border border-slate-800/40">
                <p className="text-xs text-slate-300 leading-relaxed" data-testid="why-this-wedge">{rec.why_this_wedge}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Confidence + Posture */}
        <div className="col-span-4 space-y-4">
          <Card className={`${cStyle.bg} border ${cStyle.border}`}>
            <CardContent className="pt-4 pb-3">
              <SectionLabel>Confidence</SectionLabel>
              <div className="flex items-center gap-2 mt-1">
                <ConfIcon className={`w-5 h-5 ${cStyle.text}`} />
                <span className={`text-lg font-bold ${cStyle.text}`} data-testid="confidence-label">{rec.confidence_label}</span>
                <span className="text-xs text-slate-500 font-mono">{Math.round(rec.confidence * 100)}%</span>
              </div>
            </CardContent>
          </Card>
          <Card className={`${pStyle.bg} border ${pStyle.border}`}>
            <CardContent className="pt-4 pb-3">
              <SectionLabel>Commercial Posture</SectionLabel>
              <p className={`text-sm font-semibold mt-1 ${pStyle.text}`} data-testid="posture-label">{posture.posture}</p>
              <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">{posture.reasoning}</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Confidence Drivers + Uncertainty Flags */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-[#111827] border-slate-800/60">
          <CardContent className="pt-4 pb-3">
            <SectionLabel>Confidence Drivers</SectionLabel>
            {rec.confidence_drivers.length > 0 ? (
              <ul className="space-y-1.5 mt-1">
                {rec.confidence_drivers.map((d, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-300">
                    <Zap className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                    <span className="leading-tight">{d}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-slate-500 mt-1">No strong drivers identified</p>
            )}
          </CardContent>
        </Card>
        <Card className="bg-[#111827] border-slate-800/60">
          <CardContent className="pt-4 pb-3">
            <SectionLabel>Uncertainty Flags</SectionLabel>
            {rec.uncertainty_flags.length > 0 ? (
              <ul className="space-y-1.5 mt-1">
                {rec.uncertainty_flags.map((u, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-400">
                    <EyeOff className="w-3 h-3 text-amber-500/70 mt-0.5 shrink-0" />
                    <span className="leading-tight">{u}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-slate-500 mt-1">No uncertainty flags</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* What Would Strengthen This */}
      {result.what_would_strengthen?.length > 0 && (
        <Card className="bg-sky-950/20 border-sky-800/30">
          <CardContent className="pt-4 pb-3">
            <SectionLabel>What Would Strengthen This Recommendation</SectionLabel>
            <ul className="space-y-1.5 mt-1">
              {result.what_would_strengthen.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-sky-300/80">
                  <ArrowRight className="w-3 h-3 text-sky-500 mt-0.5 shrink-0" />
                  <span className="leading-tight">{s}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Proof Assessment */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-[#111827] border-slate-800/60">
          <CardContent className="pt-4 pb-3">
            <SectionLabel><span className="flex items-center gap-1"><Unlock className="w-3 h-3 text-emerald-500" /> Proof Available</span></SectionLabel>
            <ul className="space-y-1.5 mt-1">
              {result.proof_assessment.available.map((p, i) => (
                <li key={i} className="text-xs text-slate-300 leading-tight pl-3 border-l-2 border-emerald-700/40">{p}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
        <Card className="bg-[#111827] border-slate-800/60">
          <CardContent className="pt-4 pb-3">
            <SectionLabel><span className="flex items-center gap-1"><Lock className="w-3 h-3 text-amber-500" /> Proof Still Needed</span></SectionLabel>
            <ul className="space-y-1.5 mt-1">
              {result.proof_assessment.needed.map((p, i) => (
                <li key={i} className="text-xs text-slate-400 leading-tight pl-3 border-l-2 border-amber-700/30">{p}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Economic Buyers + Expansion Path */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-[#111827] border-slate-800/60">
          <CardContent className="pt-4 pb-3">
            <SectionLabel>Economic Buyers for This Wedge</SectionLabel>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {result.economic_buyers.map((b, i) => (
                <Badge key={i} variant="outline" className="text-[10px] border-slate-700 text-slate-300 bg-slate-800/40">{b}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#111827] border-slate-800/60">
          <CardContent className="pt-4 pb-3">
            <SectionLabel>Natural Expansion Path</SectionLabel>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {result.expansion_path.map((e, i) => (
                <Badge key={i} variant="outline" className="text-[10px] border-emerald-800/40 text-emerald-400/80 bg-emerald-900/10">{e}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Comparative Reasoning */}
      {result.comparative_reasoning?.length > 0 && (
        <Card className="bg-[#111827] border-slate-800/60">
          <CardContent className="pt-4 pb-3">
            <SectionLabel>Comparative Reasoning — Why Not These</SectionLabel>
            <div className="space-y-3 mt-2">
              {result.comparative_reasoning.map((c, i) => (
                <div key={i} className="p-3 rounded-md bg-slate-900/40 border border-slate-800/30">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-300">{c.entry_point}</span>
                    <div className="flex items-center gap-2">
                      <ScoreBar score={c.score} />
                      <Badge variant="outline" className="text-[9px] border-slate-700 text-slate-500">{c.verdict}</Badge>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-1.5"><span className="text-slate-400">Why not first:</span> {c.why_not_first}</p>
                  <p className="text-xs text-slate-500 mt-0.5"><span className="text-slate-400">Would become stronger if:</span> {c.what_would_make_it_stronger}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Scores Overview */}
      <Card className="bg-[#111827] border-slate-800/60">
        <CardContent className="pt-4 pb-3">
          <SectionLabel>All Entry Points — Score Overview</SectionLabel>
          <div className="space-y-2 mt-2">
            {result.all_scores.map((s, i) => (
              <div key={s.entry_point_id} className="flex items-center gap-3">
                <span className={`text-xs w-5 text-center font-mono ${i === 0 ? "text-emerald-400" : "text-slate-600"}`}>{i + 1}</span>
                <span className={`text-xs truncate w-56 ${i === 0 ? "text-slate-200 font-medium" : "text-slate-500"}`}>{s.title}</span>
                <div className="flex-1"><ScoreBar score={s.score} /></div>
                <Badge variant="outline" className={`text-[9px] w-20 justify-center ${
                  s.confidence_label === "High" ? "border-emerald-700/40 text-emerald-400" :
                  s.confidence_label === "Medium" ? "border-amber-700/40 text-amber-400" :
                  s.confidence_label === "Low" ? "border-orange-700/40 text-orange-400" :
                  "border-red-700/40 text-red-400"
                }`}>{s.confidence_label}</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Risk Warning */}
      <Card className="bg-[#111827] border-slate-800/60">
        <CardContent className="pt-4 pb-3">
          <SectionLabel>Risk If Wrong Wedge Chosen</SectionLabel>
          <p className="text-xs text-slate-400 leading-relaxed mt-1" data-testid="risk-text">{result.risk_if_wrong_wedge}</p>
        </CardContent>
      </Card>
    </div>
  );
}


// ─── Empty State ──────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-32 text-center" data-testid="empty-state">
      <div className="w-16 h-16 rounded-full bg-slate-800/60 flex items-center justify-center mb-5">
        <Crosshair className="w-7 h-7 text-slate-600" />
      </div>
      <h3 className="text-base font-semibold text-slate-400" style={{ fontFamily: "Manrope, sans-serif" }}>
        No analysis yet
      </h3>
      <p className="text-xs text-slate-600 mt-1.5 max-w-sm">
        Describe the buyer situation on the left and the engine will determine the most commercially powerful opening proposition.
      </p>
      <p className="text-[10px] text-slate-700 mt-4">
        Or switch to the <strong className="text-slate-500">Validation Harness</strong> to test the engine against pre-built scenarios.
      </p>
    </div>
  );
}


// ─── Scenario Harness ─────────────────────────────────────────

function ScenarioHarness({ scenarios, onRun, activeId, result, loading }) {
  if (!scenarios) return <div className="text-center py-20 text-slate-500">Loading scenarios...</div>;

  const categories = ["Clear Fit", "Ambiguous", "Conflicting Signals", "No Fit"];

  return (
    <div className="space-y-6" data-testid="scenario-harness">
      <div className="grid grid-cols-12 gap-6">
        {/* Scenario List */}
        <div className="col-span-4 space-y-4">
          {categories.map(cat => {
            const items = scenarios[cat] || [];
            if (!items.length) return null;
            const cs = CATEGORY_STYLES[cat] || {};
            return (
              <div key={cat}>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={`text-[9px] ${cs.bg} ${cs.text} ${cs.border} border`}>{cat}</Badge>
                  <span className="text-[10px] text-slate-600">{items.length} scenarios</span>
                </div>
                <div className="space-y-1.5">
                  {items.map(s => (
                    <button key={s.id} onClick={() => onRun(s)}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        activeId === s.id
                          ? "bg-slate-800/60 border-emerald-700/40"
                          : "bg-[#111827] border-slate-800/40 hover:border-slate-700/60"
                      }`} data-testid={`scenario-btn-${s.id}`}>
                      <p className={`text-xs font-medium ${activeId === s.id ? "text-slate-200" : "text-slate-400"}`}>{s.title}</p>
                      <p className="text-[10px] text-slate-600 mt-0.5 line-clamp-2">{s.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Result Panel */}
        <div className="col-span-8">
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <div className="animate-pulse text-slate-500 text-sm">Running scenario...</div>
            </div>
          ) : result ? (
            <div className="space-y-4">
              {result.scenario && (
                <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-800/40">
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Scenario: {result.scenario.category}</p>
                  <p className="text-sm font-semibold text-slate-200 mt-0.5">{result.scenario.title}</p>
                  <p className="text-xs text-slate-500 mt-1">{result.scenario.description}</p>
                </div>
              )}
              <OutputPanel result={result} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-32 text-center">
              <Shield className="w-10 h-10 text-slate-700 mb-4" />
              <p className="text-sm text-slate-500">Select a scenario to test the intelligence engine</p>
              <p className="text-[10px] text-slate-600 mt-1">Scenarios span clear-fit, ambiguous, conflicting, and no-fit situations</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
